import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AddItemToCart,
  AddItemToWishList,
  GetProductVariationByVariationId,
  GetWishListItems,
  RemoveItemFromWishList,
} from "../Data";
import { toast } from "react-toastify";
import { AiOutlineShoppingCart } from "react-icons/ai";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Thumbs } from "swiper";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/thumbs";
import { Global } from "../Variables/Global";
import Cookies from "js-cookie";
import "./SingleProduct.css";

const SingleProduct = () => {
  const [details, setDetails] = useState({});
  const [att, setAtt] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [WishListItems, setWishListItems] = useState([]);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [isLogged, setIsLogged] = useState(false);

  const navigate = useNavigate();
  const params = useParams();
  const token = Cookies.get("CustomerToken");

  // ===================== SAFE DERIVED VALUES =====================
  const productNameSafe = details?.ProductName ?? "";

  const productSlug = useMemo(
    () => productNameSafe.replace(/ /g, "-"),
    [productNameSafe]
  );

  const images = useMemo(
    () =>
      [
        details?.Image0,
        details?.Image1,
        details?.Image2,
        details?.Image3,
        details?.Image4,
      ].filter(img => img && img !== "null" && img !== "undefined"),
    [details]
  );

  // ===================== WISHLIST =====================
  const isInWishlist = WishListItems.some(
    item =>
      item.ProductId === details.ProductId &&
      item.DefaultProductVariationId === details.ProductVariationId
  );

  const HandleAddToWishList = () => {
    if (!isLogged) return navigate("/Authentication");

    const WishlistId = Cookies.get("WishList");
    if (!WishlistId) return;

    AddItemToWishList(
      WishlistId,
      details.ProductId,
      details.ProductVariationId,
      token
    )
      .then(r => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then(() => {
        toast.success("Item added to wishlist");
        setWishListItems(prev => [
          ...prev,
          {
            ProductId: details.ProductId,
            DefaultProductVariationId: details.ProductVariationId,
          },
        ]);
      })
      .catch(() => toast.error("Could Not Add"));
  };

  const HandleRemoveFromWishList = () => {
    if (!isLogged) return navigate("/Authentication");

    const WishlistId = Cookies.get("WishList");
    if (!WishlistId) return;

    RemoveItemFromWishList(details.ProductId, WishlistId, token)
      .then(r => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then(() => {
        toast.success("Item Removed from wishlist");
        setWishListItems(prev =>
          prev.filter(
            i =>
              !(
                i.ProductId === details.ProductId &&
                i.DefaultProductVariationId === details.ProductVariationId
              )
          )
        );
      })
      .catch(() => toast.error("Could Not Remove"));
  };

  // ===================== DATA =====================
  useEffect(() => {
    setIsLogged(!!token);

    GetProductVariationByVariationId(params.ProductVariationId).then(setDetails);

    fetch(
      `${Global.API_URL}Attributes/GetDifferentsProductAttributesValues/${params.ProductId}`
    )
      .then(r => r.json())
      .then(setAtt);

    if (Cookies.get("WishList")) {
      GetWishListItems(Cookies.get("WishList"), 3, token).then(r =>
        setWishListItems(r.WishlistItems)
      );
    }
  }, [params.ProductVariationId]);

  const AddToCart = () => {
    if (!isLogged) return navigate("/Authentication");

    AddItemToCart(
      Cookies.get("Cart"),
      details.ProductId,
      details.ProductVariationId,
      quantity,
      token
    ).then(() => toast.success("Added to cart"));
  };

  return (
    <div className="single-product-page">
      {/* Breadcrumb */}
      <nav className="breadcrumb-wrapper">
        <div className="container">
          <ol className="breadcrumb-list">
            <li className="breadcrumb-item">
              <Link to="/" className="breadcrumb-link">
                <i className="fa fa-home"></i>
                <span>Home</span>
              </Link>
            </li>
            <li className="breadcrumb-separator">/</li>
            <li className="breadcrumb-item active">
              {productNameSafe}
            </li>
          </ol>
        </div>
      </nav>

      <div className="container">
        <div className="row gx-5">
          {/* Images */}
          <div className="col-lg-6">
            <div className="product-gallery">
              {images.length > 0 && (
                <Swiper
                  thumbs={{
                    swiper:
                      thumbsSwiper && !thumbsSwiper.destroyed
                        ? thumbsSwiper
                        : null,
                  }}
                  modules={[FreeMode, Thumbs]}
                >
                  {images.map((img, i) => (
                    <SwiperSlide key={i}>
                      <img src={Global.PHOTO_URL + img} alt={productNameSafe} />
                    </SwiperSlide>
                  ))}
                </Swiper>
              )}

              {images.length > 1 && (
                <Swiper
                  onSwiper={setThumbsSwiper}
                  slidesPerView={4}
                  spaceBetween={10}
                  freeMode
                  watchSlidesProgress
                  modules={[FreeMode, Thumbs]}
                  className="thumbs"
                >
                  {images.map((img, i) => (
                    <SwiperSlide key={i}>
                      <img src={Global.PHOTO_URL + img} alt={productNameSafe} />
                    </SwiperSlide>
                  ))}
                </Swiper>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="col-lg-6">
            <div className="product-details">
              <h1>{productNameSafe}</h1>

              {details?.InStock === false && (
                <div className="out-of-stock-flag">
                  Out of Stock
                </div>
              )}

              <div className="price">
                {details.SalePrice > 0 ? (
                  <>
                    <span className="sale">{details.SalePrice}</span>
                    <span className="regular">{details.RegularPrice}</span>
                  </>
                ) : (
                  <span className="sale">{details.RegularPrice}</span>
                )}
              </div>

              <div
                className="description"
                dangerouslySetInnerHTML={{
                  __html: details.Description || "",
                }}
              />

              <div className="variations">
                {att.map((a, i) => (
                  <Link
                    key={i}
                    to={`/DetailedProduct/${productSlug}/${params.ProductId}/${a.ProductVarationId}`}
                    className={`chip ${a.ProductVarationId == params.ProductVariationId
                      ? "active"
                      : ""
                      }`}
                  >
                    {a.AttributeValueName}
                  </Link>
                ))}
              </div>

              <div className="cart-row">
                <input
                  type="number"
                  min="1"
                  value={quantity}
                  disabled={!details?.InStock}
                  onChange={e => setQuantity(e.target.value)}
                />
                <button
                  onClick={AddToCart}
                  disabled={!details?.InStock}
                  className={!details?.InStock ? "disabled-btn" : ""}
                >
                  <AiOutlineShoppingCart />
                  {details?.InStock ? " Add to Cart" : " Out of Stock"}
                </button>
              </div>

              {isInWishlist ? (
                <button className="wishlist" onClick={HandleRemoveFromWishList}>
                  ♥ Remove from Wishlist
                </button>
              ) : (
                <button className="wishlist" onClick={HandleAddToWishList}>
                  ♡ Add to Wishlist
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleProduct;
